import React, { Component, Suspense } from 'react';
import { observer } from 'mobx-react';
import { Button, Card, Dialog, Flex, FlexColumn, Heading, Menu, MenuItem, Spinner, Text } from 'core/components';
import TextDiffViewer from 'app/views/settings/ai/TextDiffViewer';
import RunbookRevisionCollection from 'app/views/core/runbooks/RunbookRevisionCollection';
import { fields } from 'app/forms/config/runbook';
import RunbookComment from './RunbookComment';

@observer
class RunbookHistoryDialog extends Component {
  state = {
    revisionCollection: undefined,
    selectedRevision: undefined,
    loading: true,
    viewMode: 'diff' // 'diff', 'before', 'after'
  };

  componentDidMount() {
    const { model } = this.props;
    if (model) {
      const revisionCollection = new RunbookRevisionCollection([], { runbookId: model.get('id') });
      revisionCollection.fetch().then(() => {
        this.setState({ revisionCollection, selectedRevision: revisionCollection?.at(0), loading: false });
      });
    }
  }

  onRevertClick = () => {
    const { model, onClose } = this.props;
    const { selectedRevision } = this.state;
    return model.save({ ...selectedRevision.get(), comment: 'Updated to previous revision' }).then(onClose);
  };

  get revisionList() {
    const { model: currentRevision, canRevert } = this.props;
    const { revisionCollection, selectedRevision } = this.state;
    const isCurrentRevisionSelected = selectedRevision?.revisionId === currentRevision?.revisionId;

    return (
      <Card display="flex" flexDirection="column" borderRadius={0} flexShrink={0} width={300} maxHeight="100%">
        <Flex alignItems="center" justifyContent="flex-start" px={2} py={1}>
          <Heading level={5} pt={1}>
            Select a revision
          </Heading>
        </Flex>
        <Menu flex={1} height="100%" size="large" style={{ overflowY: 'auto' }}>
          {revisionCollection?.map((model) => {
            const isSelected = model.revisionId === selectedRevision?.revisionId;
            const comment = model.get('comment');
            return (
              <MenuItem
                key={model.revisionId}
                mx={1}
                text={
                  <FlexColumn>
                    <Text as="div">{model.timeAgo}</Text>
                    <Flex alignItems="center" justifyContent="flex-start" gap="2px" whiteSpace="pre-wrap">
                      <Text as="div" small muted whiteSpace="normal">
                        {model.revisionAuthor ? `${model.revisionAuthor} • ` : ''}
                        <RunbookComment comment={comment} />
                      </Text>
                    </Flex>
                  </FlexColumn>
                }
                active={isSelected}
                onClick={() => this.setState({ selectedRevision: model })}
              />
            );
          })}
        </Menu>
        {canRevert && (
          <Flex alignItems="flex-end" justifyContent="flex-end" p={2}>
            <Button intent="primary" disabled={isCurrentRevisionSelected} onClick={this.onRevertClick}>
              Revert
            </Button>
          </Flex>
        )}
      </Card>
    );
  }

  render() {
    const { isOpen } = this.props;
    const { revisionCollection, selectedRevision, loading, viewMode } = this.state;
    const prevRevision = revisionCollection?.getPreviousRevision(selectedRevision);

    return (
      <Dialog
        {...this.props}
        title="Runbook Revision History"
        height="90vh"
        isOpen={isOpen}
        top={40}
        maxWidth={1100}
        width="80vw"
        position="fixed"
      >
        <Dialog.Body
          style={{
            padding: 0,
            overflow: 'hidden',
            borderRadius: 'inherit'
          }}
        >
          <Suspense fallback={<Spinner />} loading={loading}>
            <Flex width="100%" height="100%">
              <FlexColumn flex={3} p={2} style={{ overflowY: 'auto', maxHeight: '100%' }}>
                {Object.entries(fields)
                  .filter(([field]) => field !== 'comment')
                  .map(([field, fieldInfo]) => (
                    <React.Fragment key={field}>
                      <Text as="div" fontWeight="bold" mt={2}>
                        {fieldInfo.label}
                      </Text>
                      <TextDiffViewer
                        oldText={prevRevision?.get(field)}
                        newText={selectedRevision?.get(field)}
                        viewMode={viewMode}
                        key={`${field}-${selectedRevision?.revisionId}-${viewMode}`}
                      />
                    </React.Fragment>
                  ))}
              </FlexColumn>

              {this.revisionList}
            </Flex>
          </Suspense>
        </Dialog.Body>
      </Dialog>
    );
  }
}

export default RunbookHistoryDialog;
